/**
 * Показ свода встреч с контрагентами.
 *
 * Чистый модуль без React по правилу lib/*.ts: vitest берёт только `*.test.ts`,
 * компонентных тестов в проекте нет, поэтому всё, что решает, ЧТО за число видит
 * оператор, живёт здесь.
 *
 * # Что здесь решается
 *
 * 1. ДВЕ РОЛИ НЕ СКЛАДЫВАЮТСЯ. «Принял наш налив» (мы били, он стоял) и «снял
 *    нас» (мы стояли, бил он) — это разные контрагенты по поведению, и одна
 *    сумма стёрла бы ровно то, ради чего вкладка сделана. Впитывающему мейкеру
 *    можно налить и ждать; тому, кто нас снимает, — нельзя.
 *
 * 2. СРЕДНЯЯ ЦЕНА — ПРОИЗВОДНАЯ. Она не приезжает с сервера и не должна: это
 *    нотионал, делённый на шеры, и второе место расчёта разошлось бы с первым.
 *
 * 3. ПРОЧЕРК ВМЕСТО НУЛЯ. Отсутствие блока `flow` означает «кошелёк не обходили»
 *    — оборот ниже порога либо очередь не дошла, — а не «наторговал ноль». Та же
 *    причина, по которой в ADR-0007 `unknown` отделён от `noise`.
 *
 * 4. НЕВИДИМЫЕ ВСТРЕЧИ НАЗЫВАЮТСЯ ВСЛУХ. Наша мейкерская сделка без
 *    восстановленного агрессора в таблицу не попадает: кошелька у неё нет вовсе
 *    (ADR-0008). При выключенном восстановлении это ВСЕ мейкерские сделки, то
 *    есть половина свода отсутствует — и молча.
 *
 * 5. РЕЗУЛЬТАТ ВСТРЕЧ ИДЁТ ВМЕСТЕ С ГЛУБИНОЙ. «Мы против него +$12» осмысленно
 *    ровно настолько, насколько известно, по скольким встречам это посчитано:
 *    непогашенное окно исхода не имеет, и сумма по трём встречам из тридцати
 *    выглядит как итог всех тридцати. Та же дисциплина, что у числа суток рядом
 *    с PnL коротких рынков.
 */

import type {
  CounterpartiesStats,
  CounterpartyRow,
  WalletTier,
} from "../api/types";
import { plural, shortId } from "./format";
import { div, fixed, parse, toFixed } from "./money";
// PnL и РОЙ считаются ТЕМИ ЖЕ функциями, что в панели накопления, а не своими
// копиями: это одни и те же величины из одного блока WalletFlow, и вторая
// реализация разошлась бы с первой на первом же изменении формулы — оператор
// увидел бы у кошелька разный РОЙ на двух экранах.
import { paidCells, returnOnInvested, signedDollars } from "./participants";
import { formatWindow, parseSeriesSlug } from "./series";
import { formatDollars, formatShares } from "./tape";

/** Строка вкладки контрагентов. */
export interface CounterpartyLine {
  wallet: string;
  /** Имя с биржи. Пустая строка — не знаем; подписи тогда нет вовсе. */
  name: string;
  tier: WalletTier | undefined;

  /** Он ПРИНЯЛ наш налив: мы били, он стоял в стакане. Пусто — такого не было. */
  took: string | null;
  tookUSD: string | null;
  /** Он СНЯЛ нас: мы стояли, бил он. Пусто — такого не было. */
  hit: string | null;
  hitUSD: string | null;

  /** Обе половины в долларах — по этому числу и отсортирована таблица. */
  totalUSD: string;
  /**
   * Средняя цена, по которой он забирал у нас: нотионал обеих половин, делённый
   * на шеры обеих половин.
   *
   * Пусто, когда шеров ноль. Число справочное и грубое: половины считаются на
   * разных токенах (его — точно, наша — по нашему филлу), и складывать их цены
   * строго нельзя. Но вопрос «по чём он у меня берёт» задают именно так.
   */
  avgPrice: string | null;

  /** Наших сделок с ним и по скольким рынкам мы его встречали. */
  trades: number;
  markets: number;

  /**
   * НАШ результат встреч с ним — со знаком. null означает «не посчитан», а не
   * ноль: ни одно из окон, где мы с ним виделись, ещё не погасилось.
   */
  ourPnl: string | null;
  /** По скольким встречам он посчитан и сколько ещё ждут исхода. */
  settled: number;
  unsettled: number;
  /** Счёт встреч словами: «выиграно 3 · проиграно 1». Пусто — считать нечего. */
  record: string;

  /** Когда встретились впервые и в последний раз, ISO как с провода. */
  firstAt: string;
  lastAt: string;

  /** Статистика коротких рынков. Все null вместе: «кошелька не обходили». */
  bets: number | null;
  pnl: string | null;
  roi: string | null;
  /** Сколько СУТОК стоит за pnl и roi. Ноль — не обходили. */
  depthDays: number;
  depthLabel: string;
  partial: boolean;
  /** Сколько суток окна записаны прочерком и в pnl не вошли. */
  unusableDays: number;
  /**
   * Сколько ему ДОПЛАТИЛИ помимо сделок за то же окно.
   *
   * Здесь у колонки есть смысл, которого нет на других экранах: вкладка отвечает
   * на вопрос «кто из нас прав», а наш плюс против его минуса перестаёт быть
   * игрой с нулевой суммой ровно потому, что третья сторона доплачивает обоим.
   */
  paid: string | null;
  paidTitle: string;
}

/** Периоды свода. Часы — контракт с бэкендом (?since_hours=). */
export const CP_PERIODS: readonly { hours: number; label: string }[] = [
  { hours: 1, label: "час" },
  { hours: 24, label: "сутки" },
  { hours: 24 * 7, label: "неделя" },
  { hours: 24 * 30, label: "месяц" },
];

/**
 * Таблица свода, сверху вниз.
 *
 * Порядок задаёт бэкенд (по сумме взятого у нас, убывая) и здесь не
 * пересортировывается: он же определяет, кто попал в отсечку, и вторая
 * сортировка означала бы, что верхние строки экрана взяты не из верхних строк
 * выдачи. То же правило, что в lib/participants.ts.
 */
export function counterpartyLines(
  p: CounterpartiesStats | undefined,
): CounterpartyLine[] {
  if (!p) return [];
  return p.rows.map(lineOf);
}

function lineOf(row: CounterpartyRow): CounterpartyLine {
  const tookShares = parse(row.took) ?? 0;
  const hitShares = parse(row.hit) ?? 0;
  const tookUSD = parse(row.took_usd) ?? 0;
  const hitUSD = parse(row.hit_usd) ?? 0;
  const totalUSD = tookUSD + hitUSD;
  const totalShares = tookShares + hitShares;

  return {
    wallet: row.wallet,
    name: row.name ?? "",
    tier: row.tier,
    // Ноль означает «этой ролью он с нами не встречался» — прочерк, а не «0.0».
    // Число ноль здесь читалось бы как «встречался и взял ничего».
    took: tookShares > 0 ? formatShares(row.took) : null,
    tookUSD: tookShares > 0 ? formatDollars(fixed(tookUSD)) : null,
    hit: hitShares > 0 ? formatShares(row.hit) : null,
    hitUSD: hitShares > 0 ? formatDollars(fixed(hitUSD)) : null,
    totalUSD: formatDollars(fixed(totalUSD)),
    avgPrice: averagePrice(totalUSD, totalShares),
    trades: row.trades,
    markets: row.markets,
    ...scoreCells(row),
    firstAt: row.first_at,
    lastAt: row.last_at,
    ...flowCells(row),
  };
}

/**
 * Наш счёт против этого кошелька.
 *
 * # Почему ноль здесь запрещён отдельным правилом
 *
 * Потому что «мы сыграли с ним вничью» и «ни одно из наших окон с ним ещё не
 * погасилось» — это разные ответы, а выглядят они одинаково: $0.00. Второй
 * случай на пятиминутных окнах обычен, и именно у самых свежих встреч — то есть
 * ровно у тех, ради которых вкладку и открывают. Поэтому при нулевой глубине
 * сумма прячется в прочерк.
 *
 * # Почему счёт встреч стоит отдельно от суммы
 *
 * Один крупный выигрыш и двадцать мелких проигрышей дают тот же плюс, что ровная
 * прибыль, а означают совершенно разного оппонента: первого мы обыграли один раз
 * и с тех пор кормим, второго обыгрываем регулярно.
 */
function scoreCells(
  row: CounterpartyRow,
): Pick<CounterpartyLine, "ourPnl" | "settled" | "unsettled" | "record"> {
  const settled = row.settled_trades;
  return {
    ourPnl: settled > 0 ? signedDollars(row.pnl_usd) : null,
    settled,
    // Разница, а не отдельное поле с провода: одно и то же число, посчитанное
    // дважды, разошлось бы на первой же правке запроса.
    unsettled: Math.max(0, row.trades - settled),
    record: settled > 0 ? `выиграно ${row.won} · проиграно ${row.lost}` : "",
  };
}

/** Средняя цена встреч: нотионал, делённый на шеры. */
function averagePrice(usd: number, shares: number): string | null {
  if (shares <= 0) return null;
  const price = div(usd, shares);
  if (price === null) return null;
  // Три знака: тик рынка бывает 0.001, и округление до двух склеило бы соседние
  // уровни стакана в одну цену.
  return toFixed(price, 3);
}

function flowCells(
  row: CounterpartyRow,
): Pick<
  CounterpartyLine,
  | "bets"
  | "pnl"
  | "roi"
  | "depthDays"
  | "depthLabel"
  | "partial"
  | "unusableDays"
  | "paid"
  | "paidTitle"
> {
  // Выплаты приезжают СВОИМ блоком и живут отдельно от свода: кошелёк, которого
  // обошли, мог ещё не попасть под опрос выплат, и наоборот.
  const paid = paidCells(row.incentives);
  const flow = row.flow;
  if (!flow) {
    return {
      bets: null,
      pnl: null,
      roi: null,
      depthDays: 0,
      depthLabel: "",
      partial: false,
      unusableDays: 0,
      ...paid,
    };
  }
  return {
    bets: flow.trades,
    pnl: signedDollars(flow.pnl_usd),
    // РОЙ — прибыль на доллар, ЗАНЕСЁННЫЙ покупками. Знаменателя нет — числа нет:
    // кошелёк мог только продавать полученное минтом.
    roi: returnOnInvested(flow),
    depthDays: flow.days,
    depthLabel: flow.days > 0 ? `${flow.days}д` : "",
    partial: Boolean(flow.partial),
    unusableDays: flow.unusable_days ?? 0,
    ...paid,
  };
}

/**
 * Сводка над таблицей: сколько кошельков и сколько встреч за период.
 *
 * Встречи, а не сделки: одна наша сделка бывает разделена с несколькими
 * кошельками, и складывать её несколько раз здесь правильно — вопрос «сколько раз
 * я с ним столкнулся», а не «сколько раз торговал».
 */
export function counterpartiesSummary(
  p: CounterpartiesStats | undefined,
): string {
  if (!p) return "";
  const wallets = p.rows.length;
  const met = p.rows.reduce((sum, r) => sum + r.trades, 0);
  return (
    `${wallets} ${plural(wallets, "кошелёк", "кошелька", "кошельков")} · ` +
    `${met} ${plural(met, "встреча", "встречи", "встреч")}`
  );
}

/**
 * Предупреждения о том, чего в таблице НЕТ.
 *
 * Таблица показывает, что есть, и молчит об отсутствующем — а отсутствующее здесь
 * систематическое, а не случайное: это ровно наши мейкерские сделки, то есть
 * встречи с агрессорами.
 */
export function counterpartiesWarnings(
  p: CounterpartiesStats | undefined,
): string[] {
  if (!p) return [];
  const out: string[] = [];
  if (p.pending_takers > 0) {
    const n = p.pending_takers;
    out.push(
      `${n} ${plural(n, "наша сделка", "наших сделки", "наших сделок")} за период ` +
        `${plural(n, "ждёт", "ждут", "ждут")} восстановления агрессора: мы стояли в ` +
        "стакане, и адрес того, кто нас снял, приходит из цепочки позже. В таблице " +
        "таких встреч НЕТ, и свод занижен ровно на них",
    );
  }
  if (p.truncated) {
    out.push(
      "контрагентов больше, чем помещается на страницу: показаны те, кто взял у нас " +
        "больше всех",
    );
  }
  return out;
}

/**
 * Человеческое имя окна для списка встреч: «BTC 5m · 14:30–14:35».
 *
 * # Зачем оно вообще
 *
 * В раскрытой строке контрагента стоял `0x6108d59a…` — идентификатор, который не
 * говорит ни про актив, ни про время. А вопрос, который задают к списку встреч,
 * ровно такой: «на чём и когда мы с ним столкнулись». Шестнадцатеричная строка
 * отвечает на него хуже, чем ничего: её ещё и хочется куда-то скопировать.
 *
 * # Почему из slug, а не из question
 *
 * Slug машинный и разбирается на части (`btc-updown-5m-1786150800`), из которых
 * собирается ровно то, что нужно: актив, длительность, окно локальным временем.
 * Question — человеческий текст биржи («Bitcoin Up or Down — August 12, 3PM ET»),
 * он длиннее строки таблицы и время в нём в чужой зоне.
 *
 * Пустой либо неразобранный slug оставляет ОБРЕЗАННЫЙ идентификатор: рынка нет в
 * справочнике (окно, через которое трейдер не прокатывался) либо это не серия
 * «updown». Притворяться, что мы поняли чужую схему, вреднее прочерка.
 */
export function marketLabel(
  slug: string | undefined,
  conditionID: string,
  now: Date = new Date(),
): string {
  const parts = slug ? parseSeriesSlug(slug) : null;
  if (parts === null) return shortId(conditionID);
  return `${parts.asset} ${parts.duration} · ${formatWindow(parts, now)}`;
}
